import { Injectable } from '@angular/core';
import { Fault } from '@docma-mm-core/fault/fault';
import {Structure} from '@docma-mm-core/structure/structure';
import {Project} from '@docma-mm-core/project/project';
import { LoadStatusEnum } from '@docma-mm-core/model/load-state/load-state.enum';
import { filter, first, firstValueFrom, map } from 'rxjs';
import { MmProjectLoaderService } from '../mm-project/mm-project-loader.service';
import { FaultDataSearch, FaultDataSearchFilter, FaultPoolFilter, FaultPoolFilterMode } from '@docma-mm-core/fault/fault-data';
import { FilterOperatorEnum } from '@docma-mm-core/filter/filter-operator.enum';
import { FilterCriteria } from '@docma-mm-core/filter/filter-criteria';
import { FieldMinimal } from '@docma-mm-core/field/field';
import { FilterLinkOperatorEnum } from '@docma-mm-core/filter/filter-link-operator.enum';
import { FIELD_DICT_MAPPING, FieldMappingEnum } from './fields/field-mapping.enum';
import { FieldPropertyEnum } from '@docma-mm-core/field/field-property.enum';

@Injectable({
  providedIn: 'root'
})
export class MmOfflineFilterService {
  public fields?: FieldMinimal[];

  constructor(private projectLoaderService: MmProjectLoaderService) {
  }

  async applyOfflineFilter(faults: Fault[], context: {project: Project, structure: Structure}, searchParams: FaultDataSearch): Promise<Fault[]> {
    let result = faults;
    result = await this.filterByStructure(result, context.project, context.structure);
    result = await this.filterBySearchParams(result, searchParams?.Filter);
    result = await this.filterByPool(result, searchParams?.FaultPoolFilters);

    return result;
  }

  async filterByStructure(faults: Fault[], project: Project, structure: Structure): Promise<Fault[]> {
    if (structure && structure.Id !== project.Structure.Id) {
      const projectStructures = (await this.projectLoaderService.structures.pipe(filter(s => s.loadState.status === LoadStatusEnum.Loaded), first()).toPromise()).data;
      const loadedStructure = projectStructures[0].findById(structure.Id);
      faults = faults.filter(f => f.loadState.status === LoadStatusEnum.Error ||
        (f.data.Structure && f.data.Structure.Id && loadedStructure.findById(f.data.Structure.Id)));
    }

    return faults;
  }

  async filterBySearchParams(faults: Fault[], searchFilter: FaultDataSearchFilter): Promise<Fault[]> {
    if (!searchFilter?.FilterItems?.length) {
      return faults;
    }

    await this.ensureFieldDict();

    return faults.filter(fault =>
      searchFilter.FilterItems.every(filterItem => {
        const criterias = filterItem.Criterias ?? [];
        if (criterias.length === 0) {
          return true;
        }

        const groups = Object.values(
          criterias.reduce((acc, c) => {
            const key = c.SearchGroupUid;
            (acc[key] ??= []).push(c);
            return acc;
          }, {} as Record<any, FilterCriteria[]>)
        );

        return groups.every(group => {
          if (group.length === 1) {
            return this.matchesCriteria(fault, group[0]);
          }

          const operator = group[1].LinkOperator;

          return operator === FilterLinkOperatorEnum.And
            ? group.every(c => this.matchesCriteria(fault, c))
            : group.some(c => this.matchesCriteria(fault, c));
        });
      })
    );
  }

  async filterByPool(faults: Fault[], poolFilters: FaultPoolFilter[]): Promise<Fault[]> {
    if (!poolFilters || poolFilters.length === 0) {
      return faults;
    }

    return faults.filter(fault => {
      return poolFilters.every(poolFilter => {
        if (poolFilter.PoolId === undefined) {
          return true;
        }

        const hasPool = fault.data.PoolIds?.includes(poolFilter.PoolId);

        if (poolFilter.Mode === FaultPoolFilterMode.Include) {
          return hasPool;
        } else if (poolFilter.Mode === FaultPoolFilterMode.Exclude) {
          return !hasPool;
        }

        return true;
      });
    });
  }

  private matchesCriteria(fault: Fault, criteria: FilterCriteria): boolean {
    if (!criteria?.Field) {
      return true;
    }

    const field = this.fields?.find(f => f.FieldId === criteria.Field.Id);

    let faultValue;
    if (!field?.Prop) {
      if (field?.Id === FIELD_DICT_MAPPING[FieldMappingEnum.AttributeGroupImage]){
        const attribute = (fault.data as any)[FieldPropertyEnum.Attribute];
        faultValue = attribute?.AttributeGroup?.Id;
      }
      else if (criteria.Field.DefaultName === 'SearchId'){
        faultValue = fault.data?.SearchIds;
        return faultValue?.includes(+criteria.ComparativeValue) ?? false;
      }
      else {
        return false;
      }
    }
    else {
      faultValue = (fault.data as any)[field.Prop];
    }


    const compareValueRaw = criteria.ComparativeValue;
    const compareValueStrings: string[] = String(compareValueRaw)
      .split(/[\r\n,]+/)
      .map(s => s.trim())
      .filter(s => s.length > 0);

    const compareValue2 = criteria.ComparativeValue2;
    const faultValueIsEmpty = faultValue == null;
    const compareValueIsEmpty = compareValueRaw == null;

    if (faultValueIsEmpty &&  compareValueIsEmpty){
      return true;
    }

    if (faultValue == null) {
      return false;
    }

    if (faultValue instanceof Date) {

      const compare1 = compareValueStrings.length > 0 ? new Date(compareValueStrings[0]) : null;

      const compare2 = compareValue2 != null ? new Date(compareValue2) : null;

      return this.compareDate(
        faultValue,
        compare1,
        compare2,
        criteria
      );
    }

    if (typeof faultValue === 'object') {
      const compareValueNumbers: number[] = compareValueStrings
        .map(Number)
        .filter(n => !isNaN(n));
      const cmp2 = Number(compareValue2);
      return this.compareNumber(faultValue.Id, compareValueNumbers, cmp2, criteria);
    }

    if (typeof faultValue === 'number') {
      const compareValueNumbers: number[] = compareValueStrings
        .map(Number)
        .filter(n => !isNaN(n));
      const cmp2 = Number(compareValue2);

      return this.compareNumber(
        faultValue,
        compareValueNumbers,
        cmp2,
        criteria
      );
    }

    if (typeof faultValue === 'boolean') {
      const compareValue = compareValueStrings[0]?.toLowerCase() === 'true' ? true :
        compareValueStrings[0]?.toLowerCase() === 'false' ? false :
        null;
      return this.compareBoolean(
        faultValue,
        compareValue,
        criteria
      );
    }

    return this.compareString(
      String(faultValue),
      compareValueStrings,
      criteria
    );
  }

  private async ensureFieldDict(): Promise<void> {
    this.fields = await firstValueFrom(
      this.projectLoaderService.fields.pipe(
        filter(f => f.loadState.status === LoadStatusEnum.Loaded),
        map(m => m.data)
      )
    );
  }

  private compareDate(
    value: Date | null | undefined,
    compare1: Date,
    compare2: Date | null | undefined,
    criteria: FilterCriteria
  ): boolean {

    if (criteria.LogicalOperator === FilterOperatorEnum.Empty) {
      return value == null;
    }

    if (criteria.LogicalOperator === FilterOperatorEnum.NotEmpty) {
      return value != null;
    }

    if (!value || !compare1) {
      return false;
    }

    const v = this.toDateOnly(value);
    const c1 = this.toDateOnly(compare1);
    const c2 = compare2 ? this.toDateOnly(compare2) : null;

    switch (criteria.LogicalOperator) {

      case FilterOperatorEnum.Equal:
        return v === c1;

      case FilterOperatorEnum.NotEqual:
        return v !== c1;

      case FilterOperatorEnum.Greater:
        return v > c1;

      case FilterOperatorEnum.GreaterEqual:
        return v >= c1;

      case FilterOperatorEnum.Less:
        return v < c1;

      case FilterOperatorEnum.LessEqual:
        return v <= c1;

      case FilterOperatorEnum.Between:
        if (c2 == null) { return false; }
        return v >= c1 && v <= c2;

      case FilterOperatorEnum.NotBetween:
        if (c2 == null) { return false; }
        return v < c1 || v > c2;

      default:
        return false;
    }
  }

  private compareNumber(
    value: number | null | undefined,
    compare1: number[],
    compare2: number | null | undefined,
    criteria: FilterCriteria
  ): boolean {

    switch (criteria.LogicalOperator) {

      case FilterOperatorEnum.Equal:
        return value === compare1[0];

      case FilterOperatorEnum.NotEqual:
        return value !== compare1[0];

      case FilterOperatorEnum.Greater:
        return value != null && value > compare1[0];

      case FilterOperatorEnum.GreaterEqual:
        return value != null && value >= compare1[0];

      case FilterOperatorEnum.Less:
        return value != null && value < compare1[0];

      case FilterOperatorEnum.LessEqual:
        return value != null && value <= compare1[0];

      case FilterOperatorEnum.Between:
        return value != null &&
          value >= compare1[0] &&
          value <= compare2;

      case FilterOperatorEnum.NotBetween:
        return value != null &&
          (value < compare1[0] ||
          value > compare2);

      case FilterOperatorEnum.IsIn:
        return compare1.includes(value);

      case FilterOperatorEnum.Empty:
        return value == null;

      case FilterOperatorEnum.NotEmpty:
        return value != null;

      default:
        return false;
    }
  }

  private compareString(
    value: string | null | undefined,
    compare: string [],
    criteria: FilterCriteria
  ): boolean {

    const val = (value ?? '').toLowerCase();
    const cmp = (compare ?? []).map(x => x.toLowerCase());

    switch (criteria.LogicalOperator) {

      case FilterOperatorEnum.Equal:
        return val === cmp[0];

      case FilterOperatorEnum.NotEqual:
        return val !== cmp[0];

      case FilterOperatorEnum.Like:
        return val.includes(cmp[0]);

      case FilterOperatorEnum.NotLike:
        return !val.includes(cmp[0]);

      case FilterOperatorEnum.StartsWith:
        return val.startsWith(cmp[0]);

      case FilterOperatorEnum.EndsWith:
        return val.endsWith(cmp[0]);

      case FilterOperatorEnum.Empty:
        return val.length === 0;

      case FilterOperatorEnum.NotEmpty:
        return val.length > 0;

      case FilterOperatorEnum.IsIn:
        return cmp.includes(val);

      default:
        return false;
    }
  }

  private compareBoolean(
    value: boolean | null | undefined,
    compare: any,
    criteria: FilterCriteria
  ): boolean {

    switch (criteria.LogicalOperator) {

      case FilterOperatorEnum.Equal:
        return value === Boolean(compare);

      case FilterOperatorEnum.NotEqual:
        return value !== Boolean(compare);

      case FilterOperatorEnum.IsTrue:
        return value === true;

      case FilterOperatorEnum.IsFalse:
        return value === false;

      case FilterOperatorEnum.IsIn:
        return Array.isArray(criteria.ComparativeValue) &&
          criteria.ComparativeValue.includes(value);

      case FilterOperatorEnum.Empty:
        return value == null;

      case FilterOperatorEnum.NotEmpty:
        return value != null;

      default:
        return false;
    }
  }

  private toDateOnly(date: Date): number {
    return new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate()
    ).getTime();
}
}
